import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, first } from 'rxjs/operators';
import { PersonaService } from 'src/app/core/http/apiPersona/persona.service';
import { IClientContract } from 'src/app/core/interfaces/shared/bundle/IClientContract';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

export interface IClientContractFormEmitDTO {
  email: string;
  is_valid: boolean;
  client?: IClientContract;
}

@Component({
  selector: 'app-client-contract-form',
  templateUrl: './client-contract-form.component.html',
  styleUrls: ['./client-contract-form.component.scss'],
})
export class ClientContractFormComponent implements OnInit {
  @Input('data') clientFormData: IClientContract = {
    idPersona: null,
    idCliente: null,
    nombre: null,
    apellidos: null,
    dni: null,
    telefono: null,
    email: null,
    direccion: null,
    codigoPostal: null,
  };

  @Output() emitFormClientData = new EventEmitter<IClientContract>();
  @Output() isCompleted = new EventEmitter<boolean>();

  // Definición de formularios y campos de formulario
  clientForm = new FormGroup({
    /** Private properties */
    idPersona: new FormControl(this.clientFormData.idPersona),
    idCliente: new FormControl(this.clientFormData.idCliente),
    dni: new FormControl(this.clientFormData?.dni, [
      Validators.required,
      Validators.maxLength(9),
      Validators.pattern(
        '((^)(((\\d{8})([-]?)([A-Za-z]))|([X-Zx-z]{1}[-]?\\d{7}[-]?[A-Za-z]{1})))+$'
      ),
      CustomValidators.checkDNI(),
    ]),
    email: new FormControl(this.clientFormData.email, [
      Validators.required,
      Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9]+\\.[A-Za-z]{2,4}$'),
    ]),

    /** Public properties */
    nombre: new FormControl(this.clientFormData.nombre, [Validators.required]),
    apellidos: new FormControl(this.clientFormData.apellidos, [
      Validators.required,
    ]),
    telefono: new FormControl(this.clientFormData.telefono, [
      Validators.required,
      Validators.pattern(/^(\+34)?\d{9}$/),
    ]),
    direccion: new FormControl(this.clientFormData.direccion, [
      Validators.required,
    ]),
    codigoPostal: new FormControl(this.clientFormData.codigoPostal, [
      Validators.required,
      Validators.pattern(/^[0-9]+$/),
      Validators.minLength(5),
      Validators.maxLength(5),
    ]),
  });

  // Formulario específico para el campo DNI
  dniForm = new FormGroup({
    dni: new FormControl(this.clientFormData?.dni, [
      Validators.required,
      Validators.maxLength(9),
      Validators.pattern(
        '((^)(((\\d{8})([-]?)([A-Za-z]))|([X-Zx-z]{1}[-]?\\d{7}[-]?[A-Za-z]{1})))+$'
      ),
      CustomValidators.checkDNI(),
    ]),
  });

  // Método para obtener un control de formulario específico
  getControl(controlName: string, formName: string = 'clientForm') {
    return this[formName].get(controlName);
  }

  // Bandera para controlar si se ha completado la obtención de datos del DNI
  get_dni_done: boolean = false;

  // Path de traducción
  keyTranslate: string = 'clientContracts.forms.clientContractForm.label';

  constructor(private personaService: PersonaService) {}

  ngOnInit(): void {
    this.disableClientForm();
    this.setupFormListeners();

    // Si hay datos de DNI preexistentes, configurar el formulario del DNI y aplicar los datos
    if (this.clientFormData.dni) {
      this.setupDniForm();
      this.patchForms();
    }
  }

  private disableClientForm(): void {
    this.clientForm.disable();
  }

  private setupFormListeners(): void {
    this.listenClientFormValueChanges();
    this.listenDniFormValueChanges();
  }

  private setupDniForm(): void {
    this.get_dni_done = true;
    this.dniForm.disable();
  }

  private patchForms(): void {
    this.dniForm.patchValue(this.clientFormData, {
      onlySelf: true,
      emitEvent: false,
    });
    this.clientForm.patchValue(this.clientFormData, {
      onlySelf: true,
      emitEvent: false,
    });
  }

  // Escuchar cambios en el formulario del campo DNI
  listenDniFormValueChanges(): void {
    this.dniForm.valueChanges.pipe(debounceTime(2000)).subscribe((formData) => {
      if (this.dniForm.enabled) {
        const dniValue = formData.dni;
        this.get_dni_done = false;
        this.resetNotDniFormControls();
        this.enableDniFormControls();

        if (this.dniForm.valid) {
          this.fetchPersonData(dniValue);
        } else {
          console.log(this.dniForm);
          this.emitFormClientData.emit(null);
        }
      }
    });
  }

  // Escuchar cambios en el formulario principal
  listenClientFormValueChanges(): void {
    this.clientForm.valueChanges
      .pipe(debounceTime(2000))
      .subscribe((formData) => {
        if (this.clientForm.enabled && this.clientForm.valid) {
          this.emitFormClientData.emit(formData);
          this.isCompleted.emit(true);
        }
      });
  }

  // Obtener datos de persona según el valor del DNI
  private fetchPersonData(dniValue: string): void {
    this.personaService
      .getPersonByDocument(dniValue)
      .pipe(first())
      .subscribe((resp) => {
        this.get_dni_done = true;
        if (resp.data) {
          this.clientForm.patchValue(resp.data, {
            emitEvent: false,
            onlySelf: true,
          });
          this.emitFormClientData.emit(resp.data);
          this.isCompleted.emit(true);
        } else {
          const patch: IClientContract = { dni: dniValue };
          this.clientForm.patchValue(patch, {
            emitEvent: false,
            onlySelf: true,
          });
          this.enableDniFormControls();
        }
      });
  }

  private resetNotDniFormControls(): void {
    this.clientForm.reset({}, { emitEvent: false, onlySelf: true });
  }

  private enableDniFormControls(): void {
    this.clientForm.enable({ emitEvent: false, onlySelf: true });
  }
}
